import React, { useState, useRef, useEffect } from 'react';

export interface TabToggleProperties {
  tabs: string[]; // List of tab names
  defaultTab?: string; // Optional default
  onChange?: (tab: string) => void; // Callback to parent
}

/**
 * Tab toggle component
 */
function TabToggle({ tabs, defaultTab, onChange }: TabToggleProperties) {
  // Ensure defaultTab is valid; otherwise fall back to first tab
  const initialTab = defaultTab && tabs.includes(defaultTab) ? defaultTab : tabs[0];

  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [underlineStyle, setUnderlineStyle] = useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });

  const tabReferences = useRef<(HTMLButtonElement | null)[]>([]);

  // Adjust underline position dynamically
  useEffect(() => {
    const index = tabs.indexOf(activeTab);
    const currentTab = tabReferences.current[index];
    if (currentTab) {
      setUnderlineStyle({
        left: currentTab.offsetLeft,
        width: currentTab.offsetWidth,
      });
    }
    onChange?.(activeTab);
  }, [activeTab, tabs, onChange]);

  return (
    <div className="tab-toggle" role="tablist">
      {tabs.map((tab, index) => (
        <button
          key={tab}
          type="button"
          role="tab"
          aria-selected={activeTab === tab}
          ref={(element) => {
            tabReferences.current[index] = element;
          }}
          className={`tab ${activeTab === tab ? 'active' : ''}`}
          onClick={() => setActiveTab(tab)}>
          {tab}
        </button>
      ))}
      <span
        className="underline"
        style={{ left: underlineStyle.left, width: underlineStyle.width }}
      />
    </div>
  );
}

export default TabToggle;

TabToggle.defaultProps = {
  defaultTab: '',
  onChange: null,
};
